type ClientErrorLevel = 'error' | 'warn' | 'info' | 'debug' | 'log'

type ClientErrorPayload = {
  level?: ClientErrorLevel
  source?: string
  message: string
  stack?: string
  url?: string
  page?: string
  route?: string
  filename?: string
  line?: number
  column?: number
  metadata?: Record<string, unknown>
}

const REPORT_ENDPOINT = '/api/public/client-errors'
const recentFingerprints = new Map<string, number>()
const MAX_FINGERPRINTS = 100
const DUPLICATE_WINDOW_MS = 15000

let reporterInitialized = false
let originalConsoleError: typeof console.error | null = null
let originalConsoleWarn: typeof console.warn | null = null
let originalConsoleInfo: typeof console.info | null = null
let originalConsoleLog: typeof console.log | null = null
let originalConsoleDebug: typeof console.debug | null = null

const getStorageValue = (key: string) => {
  try {
    return window.localStorage.getItem(key) || undefined
  }
  catch {
    return undefined
  }
}

const isVerboseConsoleEnabled = () => {
  return getStorageValue('clientErrorReporterVerbose') === '1'
}

const pruneFingerprints = (now: number) => {
  for (const [fingerprint, timestamp] of recentFingerprints.entries()) {
    if (now - timestamp > DUPLICATE_WINDOW_MS)
      recentFingerprints.delete(fingerprint)
  }

  while (recentFingerprints.size > MAX_FINGERPRINTS) {
    const firstKey = recentFingerprints.keys().next().value
    if (!firstKey)
      break

    recentFingerprints.delete(firstKey)
  }
}

const shouldReport = (payload: ClientErrorPayload) => {
  const now = Date.now()
  pruneFingerprints(now)

  const fingerprint = [
    payload.source || 'browser',
    payload.message,
    payload.filename || '',
    payload.line || '',
    payload.column || '',
    payload.url || window.location.href,
  ].join('|')

  const previous = recentFingerprints.get(fingerprint)
  if (previous && now - previous < DUPLICATE_WINDOW_MS)
    return false

  recentFingerprints.set(fingerprint, now)

  return true
}

const normalizeError = (value: unknown) => {
  if (value instanceof Error) {
    return {
      message: value.message || value.name || 'Unknown error',
      stack: value.stack,
    }
  }

  if (typeof value === 'string')
    return { message: value }

  try {
    return { message: JSON.stringify(value) }
  }
  catch {
    return { message: String(value) }
  }
}

const serializeConsoleArgs = (args: unknown[]) => {
  const parts = args.map(arg => {
    if (arg instanceof Error)
      return arg.stack || `${arg.name}: ${arg.message}`

    if (typeof arg === 'string')
      return arg

    try {
      return JSON.stringify(arg)
    }
    catch {
      return String(arg)
    }
  })

  return parts.filter(Boolean).join(' | ') || 'console.error called without message'
}

const sendPayload = (payload: ClientErrorPayload) => {
  if (typeof window === 'undefined' || !shouldReport(payload))
    return

  const body = JSON.stringify({
    level: payload.level || 'error',
    source: payload.source || 'tenant-spa',
    message: payload.message.slice(0, 4000),
    stack: payload.stack?.slice(0, 20000),
    url: payload.url || window.location.href,
    page: payload.page || document.title || '',
    route: payload.route || `${window.location.pathname}${window.location.hash}`,
    filename: payload.filename?.slice(0, 1000),
    line: payload.line,
    column: payload.column,
    metadata: payload.metadata || {},
    user_agent: navigator.userAgent,
  })

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }

  const tenantId = getStorageValue('tenantId')
  const activeAppId = getStorageValue('activeAppId')

  if (tenantId)
    headers['X-Tenant-ID'] = tenantId

  if (activeAppId)
    headers['X-App-ID'] = activeAppId

  if (window.location.hostname)
    headers['X-Tenant-Domain'] = window.location.hostname

  void fetch(REPORT_ENDPOINT, {
    method: 'POST',
    headers,
    body,
    credentials: 'include',
    keepalive: true,
  }).catch(() => {
    // Avoid secondary console noise when the logger itself cannot send.
  })
}

export const initClientErrorReporter = (source = 'tenant-spa') => {
  if (typeof window === 'undefined' || reporterInitialized)
    return

  reporterInitialized = true

  window.addEventListener('error', event => {
    const normalized = normalizeError(event.error || event.message)
    sendPayload({
      source,
      level: 'error',
      message: normalized.message,
      stack: normalized.stack,
      filename: event.filename,
      line: event.lineno,
      column: event.colno,
      metadata: {
        type: 'window.error',
      },
    })
  })

  window.addEventListener('unhandledrejection', event => {
    const normalized = normalizeError(event.reason)
    sendPayload({
      source,
      level: 'error',
      message: normalized.message,
      stack: normalized.stack,
      metadata: {
        type: 'unhandledrejection',
      },
    })
  })

  if (!originalConsoleError) {
    originalConsoleError = console.error.bind(console)

    console.error = (...args: unknown[]) => {
      sendPayload({
        source,
        level: 'error',
        message: serializeConsoleArgs(args),
        metadata: {
          type: 'console.error',
        },
      })

      originalConsoleError?.(...args)
    }
  }

  if (!originalConsoleWarn) {
    originalConsoleWarn = console.warn.bind(console)

    console.warn = (...args: unknown[]) => {
      sendPayload({
        source,
        level: 'warn',
        message: serializeConsoleArgs(args),
        metadata: {
          type: 'console.warn',
        },
      })

      originalConsoleWarn?.(...args)
    }
  }

  if (!originalConsoleInfo) {
    originalConsoleInfo = console.info.bind(console)

    console.info = (...args: unknown[]) => {
      if (isVerboseConsoleEnabled()) {
        sendPayload({
          source,
          level: 'info',
          message: serializeConsoleArgs(args),
          metadata: {
            type: 'console.info',
            verbose: true,
          },
        })
      }

      originalConsoleInfo?.(...args)
    }
  }

  if (!originalConsoleLog) {
    originalConsoleLog = console.log.bind(console)

    console.log = (...args: unknown[]) => {
      if (isVerboseConsoleEnabled()) {
        sendPayload({
          source,
          level: 'log',
          message: serializeConsoleArgs(args),
          metadata: {
            type: 'console.log',
            verbose: true,
          },
        })
      }

      originalConsoleLog?.(...args)
    }
  }

  if (!originalConsoleDebug) {
    originalConsoleDebug = console.debug.bind(console)

    console.debug = (...args: unknown[]) => {
      if (isVerboseConsoleEnabled()) {
        sendPayload({
          source,
          level: 'debug',
          message: serializeConsoleArgs(args),
          metadata: {
            type: 'console.debug',
            verbose: true,
          },
        })
      }

      originalConsoleDebug?.(...args)
    }
  }
}
